import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { debounce } from '../utils';

const MIN_LOGO_WIDTH = 100;

const LogoComponent = ({
  isBuilder, handleAddFormLogo, formWidth, formLogoProperties, usePreLoad
}) => {
  const { url = '', width = MIN_LOGO_WIDTH, height = 100, altText = 'Form'} = formLogoProperties.image;
  const [imageLoaded, setImageLoaded] = useState(false);
  
  useEffect(() => {
    const img = new Image();
    img.src = url;

    img.onload = () => {
      setImageLoaded(true);
    };

    img.onerror = (error) => {
      console.error('Error loading image:', error);
      setImageLoaded(true);
    };

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, []);

  return React.useMemo(() => {
    const logoWidth = width > formWidth ? formWidth : width;

    const logoImage = (
      <img
        src={url}
        className="form-page-cover-image"
        width={logoWidth}
        height={height}
        alt={`${altText} Logo`}
        style={{
          aspectRatio: `${width}/${height}`,
        }}
      />
    );

    return (
      <div
        className="form-page-cover-image-wrapper"
        style={{ maxWidth: `${formWidth}px` }}
      >
        {isBuilder ? (
          <button
            type="button"
            className="add-form-logo"
            onClick={handleAddFormLogo}
            style={{
              visibility: usePreLoad && imageLoaded ? "visible" : "hidden",
            }}
          >
            {logoImage}
          </button>
        ) : (
          logoImage
        )}
      </div>
    );
  }, [formWidth, url, width, height, imageLoaded, altText]);
};

const FormLogo = ({
  isBuilder,
  handleAddFormLogo,
  formWidth,
  formLogoProperties,
  logoTitle,
  updateFormProperty,
  styleJSON, 
  usePreLoad = false
}) => {

  const wrapperClass = `form-cover-wrapper form-has-cover form-page-cover-image-align-${formLogoProperties.topPosition?.toLowerCase()}`;

  const handleFormWidthChange = onUpdating => {
    if (!onUpdating) {
      const { width, height } = formLogoProperties?.image || {};
      if (width > formWidth) {
        const ratio = height / width;
        const logoWidth = formWidth > MIN_LOGO_WIDTH ? formWidth : MIN_LOGO_WIDTH;

        styleJSON['@formCoverImgWidth'] = logoWidth;
        styleJSON['@formCoverImgHeight'] = logoWidth * ratio;

        updateFormProperty({
          styleJSON
        }, true, true);
      }
    }
  };

  useEffect(() => {
    let onUpdating = false;

    const debouncedHandleFormWidthChange = debounce(() => handleFormWidthChange(onUpdating), 500);

    if (!onUpdating) {
      debouncedHandleFormWidthChange();
    }

    return () => {
      onUpdating = true;
    };
  }, [formWidth, formLogoProperties?.image?.width]);

  return (
    <div id='formCoverLogo' style={{ marginBottom: formLogoProperties.bottomMargin }} className={wrapperClass}>
      <LogoComponent
        isBuilder={isBuilder}
        logoTitle={logoTitle}
        handleAddFormLogo={handleAddFormLogo}
        formWidth={formWidth}
        formLogoProperties={formLogoProperties}
        usePreLoad={usePreLoad}
      />
    </div>
  );
};

FormLogo.defaultProps = {
  handleAddFormLogo: () => { },
  logoTitle: '',
  isBuilder: false,
  updateFormProperty: () => { },
  styleJSON: {}
};

FormLogo.propTypes = {
  isBuilder: PropTypes.bool,
  logoTitle: PropTypes.string,
  handleAddFormLogo: PropTypes.func,
  formWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  formLogoProperties: PropTypes.shape({
    bottomMargin: PropTypes.number,
    topPosition: PropTypes.string,
    image: PropTypes.shape({
      url: PropTypes.string.isRequired,
      width: PropTypes.number.isRequired,
      height: PropTypes.number.isRequired,
      altText: PropTypes.string
    }).isRequired
  }).isRequired,
  updateFormProperty: PropTypes.func,
  styleJSON: PropTypes.object,
  usePreLoad: PropTypes.bool
};

export default FormLogo;
